import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { ThumbsUp, MessageCircle, UserPlus, Bell, Mail, Settings } from "lucide-react";
import { toast } from "sonner";

const ICONS = { reaction: ThumbsUp, comment: MessageCircle, wall_post: UserPlus };
const PREF_ROWS = [
  { key: "reaction", label: "Reactions on my posts", icon: ThumbsUp },
  { key: "comment", label: "Comments on my posts", icon: MessageCircle },
  { key: "wall_post", label: "Posts on my wall", icon: UserPlus },
];

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Notifications() {
  const { user, setUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);

  const prefs = user?.notif_prefs || {};
  const digest = !!user?.notif_digest;

  const updatePref = async (patch) => {
    try {
      const { data } = await api.put("/profile", patch);
      setUser(data);
    } catch { toast.error("Could not update preferences"); }
  };

  const toggleType = (key) => {
    const current = prefs[key] !== false;
    updatePref({ notif_prefs: { ...prefs, [key]: !current } });
  };

  const toggleDigest = () => {
    updatePref({ notif_digest: !digest }).then(() =>
      toast.success(!digest ? "You'll get a daily digest instead of live pings." : "Live notifications turned back on."));
  };

  useEffect(() => {
    api.get("/notifications").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoaded(true));
    const t = setTimeout(() => { api.post("/notifications/read").catch(() => {}); }, 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-bone pt-32 pb-24">
      <div className="max-w-[720px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Activity</span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">Notifications</h1>
            </div>
            <button onClick={() => setShowPrefs((s) => !s)} data-testid="notif-prefs-toggle"
              className="inline-flex items-center gap-2 border border-charcoal/20 px-4 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors">
              <Settings size={14} /> Preferences
            </button>
          </div>
        </Reveal>

        {showPrefs && (
          <Reveal>
            <div className="mt-8 border border-charcoal/10 p-6" data-testid="notif-prefs-panel">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-charcoal/50">Notify me about</span>
              <div className="mt-4 space-y-3">
                {PREF_ROWS.map((r) => {
                  const on = prefs[r.key] !== false;
                  return (
                    <label key={r.key} className="flex items-center gap-3 cursor-pointer" data-testid={`pref-${r.key}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleType(r.key)} className="accent-navy w-4 h-4" />
                      <r.icon size={16} className="text-charcoal/60" />
                      <span className="font-sans text-sm text-charcoal/80">{r.label}</span>
                    </label>
                  );
                })}
              </div>
              <label className="flex items-center gap-3 cursor-pointer mt-5 pt-5 border-t border-charcoal/10" data-testid="pref-digest">
                <input type="checkbox" checked={digest} onChange={toggleDigest} className="accent-gold w-4 h-4" />
                <Mail size={16} className="text-charcoal/60" />
                <span className="font-sans text-sm text-charcoal/80">Daily digest email instead of live pings</span>
              </label>
            </div>
          </Reveal>
        )}

        {loaded && items.length === 0 && (
          <Reveal delay={0.1}>
            <div className="mt-14 border border-charcoal/10 p-12 text-center">
              <Bell size={34} className="mx-auto text-charcoal/30" strokeWidth={1.4} />
              <p className="font-serif text-2xl tracking-tight mt-4">You're all caught up.</p>
              <p className="font-sans text-charcoal/55 mt-2">Reactions, comments, and wall posts will show up here.</p>
            </div>
          </Reveal>
        )}

        <div className="mt-10 space-y-px bg-charcoal/10 border border-charcoal/10">
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <Link key={n.id} to={n.post_id ? `/feed` : "#"} data-testid={`notif-${n.id}`}
                className={`flex items-center gap-4 p-5 hover:bg-sand/40 transition-colors ${n.read ? "bg-bone" : "bg-gold/[0.06]"}`}>
                <div className="w-10 h-10 bg-navy text-gold flex items-center justify-center shrink-0"><Icon size={18} strokeWidth={1.6} /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-charcoal/90"><span className="font-serif">{n.actor}</span> {n.text}</p>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">{timeAgo(n.created_at)}</span>
                </div>
                {!n.read && <span className="w-2 h-2 bg-gold shrink-0" />}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
